import { useCallback, useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { connect } from 'react-redux';
import { useFocusTrap } from '@ifixit/ui/hooks/use-focus-trap';
import { BreakpointsContext } from 'Shared/utils/breakpoints';
import MenuIcon from './components/menu_icon';
import MobileMenu from './components/mobile_menu';
import Dropdowns from './components/dropdowns';
import InstantSearchBar from './components/instant_search_bar';
import InstantMobileSearch from './components/mobile_search/instant-search';
import { LocationProvider } from './components/location/location-provider';
import * as HeaderActions from './actions';
import { useModalIsOpen } from './shared-state';

interface HeaderInteractiveProps {
   headerState: any;
   toggleMobileMenu: () => void;
   dropdowns: any;
   isOnCart: boolean;
   links: any;
   langLinks: Record<string, string>;
}

const mapStateToProps = (state: any) => ({
   headerState: state.headerState,
});

const mapDispatchToProps = {
   toggleMobileMenu: HeaderActions.toggleMobileMenu,
};

const HeaderInteractive = ({
   headerState,
   toggleMobileMenu,
   dropdowns,
   isOnCart,
   links,
   langLinks,
}: HeaderInteractiveProps) => {
   const breakpoints = useContext(BreakpointsContext);
   const [modalIsOpen] = useModalIsOpen();

   const focusTrapRef = useFocusTrap<HTMLDivElement>({
      enabled: Boolean(headerState.displayMobileMenu && !modalIsOpen),
   });

   // The mobile menu icon lives at the left edge of the header chrome,
   // before the logo. Portal into the static slot the phtml renders so
   // it sits in the right DOM position without splitting this root.
   const [menuIconSlot, setMenuIconSlot] = useState<Element | null>(null);
   useEffect(() => {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- TODO: grandfathered in react-hooks 7.1 / ESLint 10 upgrade
      setMenuIconSlot(document.querySelector('.mobile-menu-icon-slot'));
   }, []);

   const mobileMenuClicked = useCallback(() => {
      toggleMobileMenu();
      document.querySelector<HTMLElement>('#menuIcon')?.focus();
   }, [toggleMobileMenu]);

   if (breakpoints.isMobileTablet()) {
      // eslint-disable-next-line react-hooks/immutability
      document.body.style.overflow = headerState.displayMobileMenu ? 'hidden' : 'auto';

      return (
         <LocationProvider langLinks={langLinks}>
            {!breakpoints.isDesktop() && (
               <>
                  {breakpoints.isMobile() && <InstantMobileSearch isOnCart={isOnCart} />}
                  {menuIconSlot &&
                     createPortal(
                        <MenuIcon
                           isActive={headerState.displayMobileMenu}
                           onClick={mobileMenuClicked}
                        />,
                        menuIconSlot
                     )}
                  <div ref={focusTrapRef}>
                     <MobileMenu
                        links={links}
                        languageDropdown={dropdowns.languageDropdown}
                        isOnCart={isOnCart}
                     />
                  </div>
               </>
            )}
            <div className="header-right-actions">
               {breakpoints.isTablet() && <InstantSearchBar isOnCart={isOnCart} />}
               <Dropdowns dropdowns={dropdowns} isOnCart={isOnCart} />
            </div>
         </LocationProvider>
      );
   }

   return (
      <LocationProvider langLinks={langLinks}>
         <div className="header-right-actions">
            <InstantSearchBar isOnCart={isOnCart} />
            <Dropdowns dropdowns={dropdowns} isOnCart={isOnCart} />
         </div>
      </LocationProvider>
   );
};

export default connect(mapStateToProps, mapDispatchToProps)(HeaderInteractive);
